import { twMerge } from 'tailwind-merge';

import { Arrow } from './Arrow';
import { PageNumber } from './PageNumber';

type PaginationItem = {
  type: 'page' | 'separator';
  value: number | string;
  isActive?: boolean;
};

const generatePaginationItems = (currentPage: number, totalPages: number): PaginationItem[] => {
  const items: PaginationItem[] = [];

  // If total pages is 7 or less, show all pages
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) {
      items.push({
        type: 'page',
        value: i,
        isActive: i === currentPage,
      });
    }
    return items;
  }

  // Always show first page
  items.push({
    type: 'page',
    value: 1,
    isActive: currentPage === 1,
  });

  // Logic for showing pages around current page
  if (currentPage <= 4) {
    // Show pages 2, 3, 4, 5
    for (let i = 2; i <= 5; i++) {
      items.push({
        type: 'page',
        value: i,
        isActive: i === currentPage,
      });
    }

    // Add separator if needed
    if (totalPages > 6) {
      items.push({ type: 'separator', value: '...' });
    }
  } else if (currentPage >= totalPages - 3) {
    // Show separator, then last 4 pages
    items.push({ type: 'separator', value: '...' });

    for (let i = totalPages - 4; i < totalPages; i++) {
      items.push({
        type: 'page',
        value: i,
        isActive: i === currentPage,
      });
    }
  } else {
    // Show separator, current page with neighbors, separator
    items.push({ type: 'separator', value: '...' });

    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      items.push({
        type: 'page',
        value: i,
        isActive: i === currentPage,
      });
    }

    items.push({ type: 'separator', value: '...' });
  }

  // Always show last page (if not already shown)
  if (totalPages > 1 && items[items.length - 1].value !== totalPages) {
    items.push({
      type: 'page',
      value: totalPages,
      isActive: currentPage === totalPages,
    });
  }

  return items;
};

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  className?: string;
  variant?: 'default' | 'analytics' | 'wheelOfFortune' | 'taskBased';
  scrollToRef?: React.RefObject<HTMLElement | null>;
  scrollBehavior?: ScrollBehavior;
};

export const Pagination = ({
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  goToPage,
  goToNextPage,
  goToPreviousPage,
  className = '',
  variant = 'default',
  scrollToRef,
  scrollBehavior = 'smooth',
}: PaginationProps) => {
  const scrollToTarget = () => {
    // setTimeout allows React to update the DOM before scrolling
    setTimeout(() => {
      scrollToRef?.current?.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    }, 0);
  };

  const handleGoToPage = (page: number) => {
    goToPage(page);
    scrollToTarget();
  };

  const handleGoToNextPage = () => {
    goToNextPage();
    scrollToTarget();
  };

  const handleGoToPreviousPage = () => {
    goToPreviousPage();
    scrollToTarget();
  };
  const defaultStyles = {
    gap: 'tw-gap-[5px] lg:tw-gap-[4.38px]',
    arrowIcon: '',
    arrowIconClassName: '',
    pageNumberWrapper: 'tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-[5px] lg:tw-gap-[4.38px]',
  };

  const variantOverrides: Record<string, Partial<typeof defaultStyles>> = {
    analytics: {
      gap: 'tw-gap-[10px] tw-font-jost',
      arrowIconClassName: 'tw-w-[22px] tw-h-[22px]',
    },
    wheelOfFortune: {
      gap: 'tw-gap-[10px] tw-font-jost',
      arrowIcon: 'lg:tw-w-auto',
      arrowIconClassName: 'tw-w-[22px] tw-h-[22px]',
    },
    taskBased: {
      gap: 'tw-gap-[5px] lg:tw-gap-[10px]',
      pageNumberWrapper: 'tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-[5px] lg:tw-gap-[10px]',
    },
  };

  const variantStyles = { ...defaultStyles, ...variantOverrides[variant] };

  const paginationItems = generatePaginationItems(currentPage, totalPages);

  return (
    <div className={twMerge('tw-flex tw-flex-row tw-items-center tw-justify-center', className, variantStyles.gap)}>
      <Arrow
        direction="left"
        onClick={handleGoToPreviousPage}
        disabled={!hasPreviousPage}
        className={variantStyles.arrowIcon}
        iconClassName={variantStyles.arrowIconClassName}
      />
      <div className={variantStyles.pageNumberWrapper}>
        {paginationItems.map((item, index) => (
          <PageNumber
            key={`${item.type}-${item.value}-${index}`}
            value={item.value}
            isActive={item.isActive}
            onClick={item.type === 'page' ? () => handleGoToPage(item.value as number) : undefined}
            variant={variant}
          />
        ))}
      </div>
      <Arrow
        direction="right"
        onClick={handleGoToNextPage}
        disabled={!hasNextPage}
        className={variantStyles.arrowIcon}
        iconClassName={variantStyles.arrowIconClassName}
      />
    </div>
  );
};
